import { useMemo } from "react";

import isNil from "lodash/isNil";

import { ProfileTestID } from "@/constants/TestID";
import MultiSelectTextField from "@/ui/composed/pickers/MultiSelectTextField";
import { FAIconName, LabeledInput, TextField } from "@/ui/core";
import type { PermissionChecker } from "@/utils/profile/permissions.util";
import { isEditable, isViewable } from "@/utils/profile/permissions.util";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = ProfileTestID.ContactInfo;

const AddressLine1Field = ({
  addressLine1,
  setAddressLine1,
  trackFieldPressIn,
  userPermissions,
}: {
  addressLine1?: null | string;
  setAddressLine1: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("address"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="Address"
        testID={joinTestIDs(baseTestID, "addressLine1-label")}
      >
        <TextField
          autoComplete="address-line1"
          icon={FAIconName.LocationDot}
          isDisabled={!isEditable(userPermissions("address"))}
          onChange={(value) => {
            setAddressLine1(value);
          }}
          onPressIn={() => trackFieldPressIn?.(AddressLine1Field.name)}
          placeholder="Address"
          testID={joinTestIDs(baseTestID, "addressLine1")}
          value={addressLine1}
        />
      </LabeledInput>
    );
  }, [addressLine1, setAddressLine1, userPermissions, trackFieldPressIn]);
};

const AddressLine2Field = ({
  addressLine2,
  setAddressLine2,
  trackFieldPressIn,
  userPermissions,
}: {
  addressLine2?: null | string;
  setAddressLine2: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("address"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="Apt, suite, etc."
        rightText="Optional"
        testID={joinTestIDs(baseTestID, "addressLine2-label")}
      >
        <TextField
          autoComplete="address-line2"
          isDisabled={!isEditable(userPermissions("address"))}
          onChange={(value) => {
            setAddressLine2(value);
          }}
          onPressIn={() => trackFieldPressIn?.(AddressLine2Field.name)}
          placeholder="Apt, suite, etc."
          testID={joinTestIDs(baseTestID, "addressLine2")}
          value={addressLine2}
        />
      </LabeledInput>
    );
  }, [addressLine2, setAddressLine2, userPermissions, trackFieldPressIn]);
};

const CityField = ({
  city,
  setCity,
  trackFieldPressIn,
  userPermissions,
}: {
  city?: null | string;
  setCity: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("address"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="City"
        testID={joinTestIDs(baseTestID, "city-label")}
      >
        <TextField
          autoComplete="postal-address-locality"
          isDisabled={!isEditable(userPermissions("address"))}
          onChange={(value) => {
            setCity(value);
          }}
          onPressIn={() => trackFieldPressIn?.(CityField.name)}
          placeholder="City"
          testID={joinTestIDs(baseTestID, "city")}
          value={city}
        />
      </LabeledInput>
    );
  }, [city, setCity, userPermissions, trackFieldPressIn]);
};

const StateField = ({
  options,
  setState,
  state,
  trackFieldPressIn,
  userPermissions,
}: {
  options: MultiSelectOption[];
  setState: (value?: string) => void;
  state?: null | string;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("address"))) {
      return null;
    }

    const defaultSelectedValues = isNil(state)
      ? []
      : [{ displayText: state, value: state }];

    return (
      <LabeledInput
        leftText="State"
        testID={joinTestIDs(baseTestID, "state-label")}
      >
        <MultiSelectTextField
          defaultSelectedValues={defaultSelectedValues}
          isDisabled={!isEditable(userPermissions("address"))}
          isSingleSelect
          modalTitle="State"
          onPressIn={() => trackFieldPressIn?.(StateField.name)}
          onValuesChange={(selection) =>
            setState(selection.length > 0 ? selection[0].value : "")
          }
          options={options}
          testID={joinTestIDs(baseTestID, "state")}
          value={state}
        />
      </LabeledInput>
    );
  }, [options, state, setState, userPermissions, trackFieldPressIn]);
};

const ZipField = ({
  setZip,
  trackFieldPressIn,
  userPermissions,
  zip,
}: {
  setZip: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
  zip?: null | string;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("address"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="ZIP Code"
        testID={joinTestIDs(baseTestID, "zip-label")}
      >
        <TextField
          autoComplete="postal-code"
          isDisabled={!isEditable(userPermissions("address"))}
          onChange={(value) => {
            setZip(value);
          }}
          onPressIn={() => trackFieldPressIn?.(ZipField.name)}
          placeholder="ZIP Code"
          testID={joinTestIDs(baseTestID, "zip")}
          value={zip}
        />
      </LabeledInput>
    );
  }, [zip, setZip, userPermissions, trackFieldPressIn]);
};

export {
  AddressLine1Field,
  AddressLine2Field,
  CityField,
  StateField,
  ZipField,
};
